import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PrinterIcon,
  ReceiptTaxIcon,
  SearchIcon,
  UserIcon,
  CalendarIcon,
  XIcon,
} from '@heroicons/react/outline';

import { useEffect, useState, useRef } from 'react';
import swal from 'sweetalert';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import LoadingBar from 'react-top-loading-bar';
import ReactPaginate from 'react-paginate';

import '../../Pagination.css';
import './ManageReceiptsView.css';

import { motion, AnimatePresence } from 'framer-motion';
import QRCode from 'qrcode';

import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';

import { format, addDays, subDays } from 'date-fns';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { DateRangePicker } from 'react-date-range';

import {
  useGetUserReceiptsOrSearchWithRangeANDOREmployeeIdMutation,
  useGetUserReceiptsPageChangeMutation,
  useGetUserReceiptsQuery,
  useGetUserReceiptsSearchMutation,
} from '../../Features/ticketsSlice';

import { useGetUserEmployeesQuery } from '../../Features/userEmployeesSlice';
import {
  selectDateRange,
  setDateRange,
} from '../../Features/date/dateSlice';

import {
  BadgeCheckIcon,
  BanknoteIcon,
  XCircleIcon,
} from 'lucide-react';

import { useTheme } from '../../ThemeContext';

function ManageReceiptsView({}) {
  const { darkMode } = useTheme();

  const user = useSelector(selectCurrentUser);
  const date_range = useSelector(selectDateRange);
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(0);

  const [numRowsPerPage, setNumRowsPerPage] = useState(10);
  const [currentItems, setCurrentItems] = useState([]);
  const [stats, setStats] = useState([]);
  const [pageCount, setPageCount] = useState(0);

  const [open, setOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const pickerRef = useRef(null);

  const [isExporting, setIsExporting] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState('all');

  const [selectedStartDate, setSelectedStartDate] = useState(false);
  const [selectedEndDate, setSelectedEndDate] = useState(false);

  const [input, setInput] = useState({
    search: '',
  });

  const [state, setState] = useState([
    {
      startDate: subDays(new Date(), 7),
      endDate: addDays(new Date(), 1),
      key: 'selection',
    },
  ]);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const printRef = useRef();

  const initialStartDate = date_range
    ? format(date_range.startDate, 'yyyy-MM-dd')
    : format(subDays(new Date(), 7), 'yyyy-MM-dd');

  const initialEndDate = date_range
    ? format(date_range.endDate, 'yyyy-MM-dd')
    : format(new Date(), 'yyyy-MM-dd');

  /*
   * ------------------------------------------------------------
   * API
   * ------------------------------------------------------------
   */

  const {
    data: receipts,
    isLoading,
    isSuccess,
  } = useGetUserReceiptsQuery({
    user_id: user.id,
    search: 0,
    pagination: '10',
    start_date: initialStartDate,
    end_date: initialEndDate,
    status: 'all',
    employee_id: '0',
  });

  const { data: employees } = useGetUserEmployeesQuery({
    account_id: user.id,
    paginate: 10,
  });

  /*
   * ------------------------------------------------------------
   * INITIAL DATA
   * ------------------------------------------------------------
   */

  useEffect(() => {
    setProgress(40);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess && receipts?.data?.tickets) {
      setCurrentItems(receipts.data.tickets.data || []);
      setStats(receipts.data.stats || []);

      setPageCount(
        Math.ceil(
          receipts.data.tickets.total /
            receipts.data.tickets.per_page
        )
      );
    }

    setProgress(100);

    const handleClickOutside = (event) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
    };
  }, [receipts, isLoading, isSuccess]);

  /*
   * ------------------------------------------------------------
   * SEARCH
   * ------------------------------------------------------------
   */

  const handleChange = (e) => {
    setInput({
      ...input,
      [e.target.name]: e.target.value,
    });
  };

  const handleSearchInput = async (e) => {
    e.preventDefault();
    submitSearch(false);
  };

  /*
   * ------------------------------------------------------------
   * EMPLOYEE
   * ------------------------------------------------------------
   */

  const handleEmployeeChange = (e, employeeId) => {
    submitSearch(employeeId);

    setInput({
      ...input,
      search: '',
    });
  };

  /*
   * ------------------------------------------------------------
   * DATE RANGE
   * ------------------------------------------------------------
   */

  const [fetchCustomData] =
    useGetUserReceiptsOrSearchWithRangeANDOREmployeeIdMutation();

  const handleSelect = async (ranges) => {
    setCurrentPage(0);

    setInput({
      ...input,
      search: '',
    });

    const { selection } = ranges;

    const searchValue = input.search ? input.search : 0;

    setState([selection]);

    if (!isSelecting) {
      setIsSelecting(true);
      return;
    }

    setIsSelecting(false);
    setOpen(false);

    const startDate = format(
      selection.startDate,
      'yyyy-MM-dd'
    );

    const endDate = format(
      selection.endDate,
      'yyyy-MM-dd'
    );

    dispatch(
      setDateRange({
        date_range: selection,
      })
    );

    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);

    try {
      setProgress(80);

      const resp = await fetchCustomData({
        user_id: user.id,
        search: searchValue,
        pagination: numRowsPerPage,
        start_date: startDate,
        end_date: endDate,
        status: 'all',
        employee_id: selectedEmployee
          ? selectedEmployee
          : '0',
      }).unwrap();

      setSelectedEmployee('');

      if (resp.success) {
        setCurrentItems(
          resp.data.tickets.data || []
        );

        setPageCount(
          Math.ceil(
            resp.data.tickets.total /
              resp.data.tickets.per_page
          )
        );

        setStats(resp.data.stats || []);
      } else {
        swal('No results found.');
      }
    } catch (err) {
      swal('Error', 'Data not found');
    } finally {
      setProgress(100);
    }
  };

  /*
   * ------------------------------------------------------------
   * TICKET MODAL
   * ------------------------------------------------------------
   */

  const openTicketModal = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const closeTicketModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
  };

  /*
   * ------------------------------------------------------------
   * PRINT
   * ------------------------------------------------------------
   */

  const handlePrint = async () => {
    if (!selectedTicket) return;

    try {
      const companyName = 'Zambia Transport Co. Ltd.';
      const companyTPIN = 'TPIN: 100-123-4567';
      const companyLogoUrl =
        'https://yourdomain.com/logo.png';

      const qrData =
        selectedTicket.ticket_token ||
        'No Token Found';

      const qrCodeUrl = await QRCode.toDataURL(
        qrData,
        {
          errorCorrectionLevel: 'H',
        }
      );

      const printWindow = window.open(
        '',
        '_blank',
        'width=400,height=600'
      );

      if (!printWindow) {
        swal(
          'Error',
          'Unable to open print window. Please allow popups.',
          'error'
        );
        return;
      }

      printWindow.document.open();

      printWindow.document.write(`
        <html>
          <head>
            <title>Bus Ticket Receipt</title>

            <style>
              @media print {
                @page {
                  size: 80mm auto;
                  margin: 0;
                }

                body {
                  margin: 0;
                  padding: 0;
                  -webkit-print-color-adjust: exact;
                }
              }

              body {
                font-family: 'Courier New', monospace;
                font-size: 11px;
                color: #000;
                box-sizing: border-box;
                width: 68mm;
                margin: 0 auto;
                padding: 6px 5mm 6px 2mm;
                word-wrap: break-word;
                overflow-wrap: break-word;
                line-height: 1.25;
              }

              .ticket {
                border-bottom: 1px dashed #000;
                padding: 8px 4px;
                width: 100%;
                box-sizing: border-box;
              }

              .header {
                text-align: center;
                margin-bottom: 6px;
              }

              .header img {
                width: 42px;
                height: 42px;
                object-fit: contain;
                display: block;
                margin: 0 auto 2px auto;
              }

              .company-name {
                font-weight: bold;
                font-size: 13px;
                text-transform: uppercase;
              }

              .tpin {
                font-size: 10px;
                color: #000;
                margin-bottom: 4px;
              }

              .title {
                text-align: center;
                font-weight: bold;
                text-transform: uppercase;
                margin: 6px 0;
                border-bottom: 1px dashed #000;
                padding: 2px 0;
              }

              .row {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin: 3px 0;
                font-size: 11px;
                line-height: 1.3;
                flex-wrap: wrap;
              }

              .label {
                color: #000;
                text-align: left;
                flex: 0 0 auto;
                margin-right: 4px;
              }

              .value {
                text-align: right;
                flex: 1 1 auto;
                word-wrap: break-word;
                overflow-wrap: break-word;
                white-space: normal;
                display: inline-block;
                margin-right: 2mm;
              }

              .line {
                border-top: 1px dashed #000;
                margin-bottom: 6px;
              }

              .linePb {
                border-top: 1px dashed #000;
                margin-bottom: 4px;
              }

              .footer {
                text-align: center;
                font-size: 10px;
                color: #555;
                margin-top: 6px;
              }

              .qr {
                display: flex;
                justify-content: center;
                margin-top: 6px;
              }

              .qr img {
                width: 100px;
                height: 100px;
              }
            </style>
          </head>

          <body>

            <div class="header">

              <img
                src="${companyLogoUrl}"
                alt="Logo"
                onerror="this.style.display='none'"
              />

              <div class="company-name">
                ${companyName}
              </div>

              <div class="tpin">
                ${companyTPIN}<br/>
                Welcome
              </div>

            </div>

            <div class="ticket">

              <div class="title">
                Ticket Receipt
              </div>

              <div class="row">
                <span class="label">Date:</span>
                <span class="value">
                  ${selectedTicket.created_at}
                </span>
              </div>

              <div class="row">
                <span class="label">Employee:</span>
                <span class="value">
                  ${selectedTicket.employee_fname}
                  ${selectedTicket.employee_lname}
                </span>
              </div>

              <div class="linePb"></div>

              <div class="row">
                <span class="label">Passenger:</span>
                <span class="value">
                  ${selectedTicket.full_name}
                </span>
              </div>

              <div class="row">
                <span class="label">Route:</span>
                <span class="value">
                  ${selectedTicket.route_name}
                </span>
              </div>

              <div class="row">
                <span class="label">Phone No:</span>
                <span class="value">
                  ${selectedTicket.phone}
                </span>
              </div>

              <div class="row">
                <span class="label">Departure:</span>
                <span class="value">
                  ${selectedTicket.departure_date}
                  @${selectedTicket.departure_time}
                </span>
              </div>

              <div class="row">
                <span class="label">Seat No:</span>
                <span class="value">
                  ${selectedTicket.seat_number || '—'}
                </span>
              </div>

              <div class="row">
                <strong class="label">Ticket No:</strong>
                <strong class="value">
                  ${selectedTicket.ticket_token}
                </strong>
              </div>

              <div class="line"></div>

              <div class="row">
                <span class="label">Payment Type:</span>
                <span class="value">
                  ${selectedTicket.payment_type_name}
                </span>
              </div>

              <div class="row">
                <span class="label">Gross Total:</span>
                <span class="value">
                  K ${(
                    Number(selectedTicket.price) +
                    Number(selectedTicket.discount)
                  ).toFixed(2)}
                </span>
              </div>

              <div class="row">
                <span class="label">Discount:</span>
                <span class="value">
                  K ${Number(
                    selectedTicket.discount
                  ).toFixed(2)}
                </span>
              </div>

              <div class="row">
                <span class="label">
                  ${selectedTicket.tax_name || 'Tax'}
                  ${
                    selectedTicket.tax_rate
                      ? `(${selectedTicket.tax_rate}%)`
                      : ''
                  }:
                </span>

                <span class="value">
                  K ${Number(
                    selectedTicket.tax || 0
                  ).toFixed(2)}
                </span>
              </div>

              <div class="row">
                <strong class="label">
                  Net Total:
                </strong>

                <strong class="value">
                  K ${Number(
                    selectedTicket.price
                  ).toFixed(2)}
                </strong>
              </div>

              <div class="row">

                <span class="label">
                  Status:
                </span>

                <span
                  class="value"
                  style="color:${
                    selectedTicket.cancelled
                      ? 'red'
                      : 'green'
                  }"
                >
                  ${
                    selectedTicket.cancelled
                      ? 'Returned'
                      : 'Valid'
                  }
                </span>

              </div>

              <div class="line"></div>

              <div class="qr">
                <img
                  src="${qrCodeUrl}"
                  alt="QR Code"
                />
              </div>

              <div class="footer">
                Thank you for choosing our service.<br/>
                Have a safe journey!
              </div>

            </div>

          </body>
        </html>
      `);

      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        }, 500);
      };
    } catch (err) {
      console.error(
        'QR Code generation failed:',
        err
      );

      swal(
        'Error',
        'Failed to generate QR code',
        'error'
      );
    }
  };

  /*
   * ------------------------------------------------------------
   * EXPORT CSV
   * ------------------------------------------------------------
   */

  const handleExportCSV = async () => {
    const searchValue = input.search
      ? input.search
      : 0;

    const startDate = selectedStartDate
      ? selectedStartDate
      : initialStartDate;

    const endDate = selectedEndDate
      ? selectedEndDate
      : initialEndDate;

    try {
      setIsExporting(true);

      const response = await fetch(
        `http://127.0.0.1:8000/user/user-receipts/${user.id}/${searchValue}/1000/${startDate}/${endDate}/all/${selectedEmployee ? selectedEmployee : '0'}`
      );

      const result = await response.json();

      if (
        !result.success ||
        !result.data?.tickets?.data?.length
      ) {
        swal(
          'No data',
          'No records found to export',
          'info'
        );

        setIsExporting(false);
        return;
      }

      const allTickets =
        result.data.tickets.data;

      const headers = [
        'ID',
        'Created At',
        'Updated At',
        'Route Name',
        'Full Name',
        'Phone',
        'Departure Time',
        'Departure Date',
        'Price',
        'Cost',
        'Change',
        'Discount',
        'Tax',
        'Tax Name',
        'Tax Rate',
        'Payment Type ID',
        'Payment Type Name',
        'User Route ID',
        'Tax ID',
        'Category ID',
        'Purpose ID',
        'Seat ID',
        'Seat Number',
        'User Employee ID',
        'Employee Shift Log ID',
        'User Routes DateTime Seating Mgt ID',
        'User ID',
        'Ticket Token',
        'Transaction ID',
        'Extended',
        'Cancelled',
        'Cancelled By',
        'Verified',
      ];

      const rows = allTickets.map((item) => [
        item.id,
        item.created_at,
        item.updated_at,
        item.route_name,
        item.full_name,
        item.phone,
        item.departure_time,
        item.departure_date,
        item.price,
        item.cost,
        item.change,
        item.discount,
        item.tax,
        item.tax_name,
        item.tax_rate,
        item.payment_type_id,
        item.payment_type_name,
        item.user_route_id,
        item.tax_id,
        item.category_id,
        item.purpose_id,
        item.seat_id,
        item.seat_number,
        item.user_employee_id,
        item.employee_shift_log_id,
        item.user_routes_date_time_seating_mgt_id,
        item.user_id,
        item.ticket_token,
        item.transaction_id,
        item.extended ? 'Yes' : 'No',
        item.cancelled ? 'Yes' : 'No',
        item.cancelled_by
          ? `${item.cancelled_by}@${item.updated_at}`
          : null,
        item.verified ? 'Yes' : 'No',
      ]);

      const csvContent = [
        headers,
        ...rows,
      ]
        .map((row) =>
          row
            .map(
              (value) =>
                `"${String(value ?? '').replace(
                  /"/g,
                  '""'
                )}"`
            )
            .join(',')
        )
        .join('\n');

      const blob = new Blob(
        [csvContent],
        {
          type: 'text/csv;charset=utf-8;',
        }
      );

      const url =
        URL.createObjectURL(blob);

      const link =
        document.createElement('a');

      link.href = url;

      link.download =
        `tickets_export_${new Date()
          .toISOString()
          .split('T')[0]}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      swal(
        'Export Complete',
        'Tickets have been exported successfully.',
        'success'
      );
    } catch (error) {
      console.error(error);

      swal(
        'Error',
        'Something went wrong while exporting data',
        'error'
      );
    } finally {
      setIsExporting(false);
    }
  };

  /*
   * ------------------------------------------------------------
   * PAGINATION
   * ------------------------------------------------------------
   */

  const [pageChange] =
    useGetUserReceiptsPageChangeMutation();

  const onChangePageRows = async (rows) => {
    setCurrentPage(0);

    const rowsPerPage =
      window.parseInt(rows);

    const searchValue = input.search
      ? input.search
      : 0;

    const startDate = selectedStartDate
      ? selectedStartDate
      : initialStartDate;

    const endDate = selectedEndDate
      ? selectedEndDate
      : initialEndDate;

    try {
      setProgress(80);

      const resp =
        await pageChange({
          user_id: user.id,
          search: searchValue,
          pagination: rowsPerPage,
          start_date: startDate,
          end_date: endDate,
          status: selectedStatus,
          employee_id: selectedEmployee
            ? selectedEmployee
            : '0',
          page: 1,
        }).unwrap();

      if (resp.success) {
        setCurrentItems(
          resp.data.tickets.data
        );

        setPageCount(
          Math.ceil(
            resp.data.tickets.total /
              resp.data.tickets.per_page
          )
        );

        setStats(resp.data.stats);

        setNumRowsPerPage(rowsPerPage);
      }

      setProgress(100);
    } catch (err) {
      setProgress(100);

      swal(
        'Error',
        err.message,
        'error'
      );
    }
  };

  const handlePageChange = async (
    currentPage
  ) => {
    setCurrentPage(currentPage);

    const pageNum =
      window.parseInt(currentPage) + 1;

    const searchValue = input.search
      ? input.search
      : 0;

    const startDate = selectedStartDate
      ? selectedStartDate
      : initialStartDate;

    const endDate = selectedEndDate
      ? selectedEndDate
      : initialEndDate;

    try {
      setProgress(80);

      const resp =
        await pageChange({
          user_id: user.id,
          search: searchValue,
          pagination: numRowsPerPage,
          start_date: startDate,
          end_date: endDate,
          status: (selectedStatus)? selectedStatus :'all',
          employee_id: selectedEmployee
            ? selectedEmployee
            : '0',
          page: pageNum,
        }).unwrap();

      if (resp.success) {
        setCurrentItems(
          resp.data.tickets.data
        );

        setPageCount(
          Math.ceil(
            resp.data.tickets.total /
              resp.data.tickets.per_page
          )
        );

        setStats(resp.data.stats);
      }

      setProgress(100);
    } catch (err) {
      setProgress(100);

      swal(
        'Error',
        'An expected error. Try again later'
      );
    }
  };

  /*
   * ------------------------------------------------------------
   * STATUS / SEARCH
   * ------------------------------------------------------------
   */

  const [searchData] =
    useGetUserReceiptsSearchMutation();

  const handleGetAllReceipts = async (
    status
  ) => {
    setCurrentPage(0);

    setInput({
      ...input,
      search: '',
    });

    setSelectedStatus(status);
    setSelectedStartDate(false);
    setSelectedEndDate(false);

    try {
      setProgress(80);

      const resp =
        await searchData({
          user_id: user.id,
          search: 0,
          pagination: numRowsPerPage,
          start_date: selectedStartDate
            ? selectedStartDate
            : initialStartDate,
          end_date: selectedEndDate
            ? selectedEndDate
            : initialEndDate,
          status: status,
          employee_id: selectedEmployee
            ? selectedEmployee
            : '0',
        }).unwrap();

      if (resp.success) {
        setCurrentItems(
          resp.data.tickets.data
        );

        setPageCount(
          Math.ceil(
            resp.data.tickets.total /
              resp.data.tickets.per_page
          )
        );

        setStats(resp.data.stats);
      }

      setProgress(100);
    } catch (err) {
      setProgress(100);

      swal(
        'No results found...'
      );
    }
  };

  const handGetReceiptsByStatus = async (
    status
  ) => {
    setCurrentPage(0);

    setInput({
      ...input,
      search: '',
    });

    const startDate = selectedStartDate
      ? selectedStartDate
      : initialStartDate;

    const endDate = selectedEndDate
      ? selectedEndDate
      : initialEndDate;

    setSelectedStatus(status);

    try {
      setProgress(80);

      const resp =
        await searchData({
          user_id: user.id,
          search: 0,
          pagination: numRowsPerPage,
          start_date: startDate,
          end_date: endDate,
          status: status,
          employee_id: selectedEmployee
            ? selectedEmployee
            : '0',
        }).unwrap();

      if (resp.success) {
        setCurrentItems(
          resp.data.tickets.data
        );

        setPageCount(
          Math.ceil(
            resp.data.tickets.total /
              resp.data.tickets.per_page
          )
        );

        setStats(resp.data.stats);
      }

      setProgress(100);
    } catch (err) {
      setProgress(100);

      swal(
        'No results found...'
      );
    }
  };

  const submitSearch = async (
    user_employee_id = false
  ) => {
    setCurrentPage(0);

    const searchValue = input.search
      ? input.search
      : 0;

    const employee_id = user_employee_id
      ? user_employee_id
      : '0';

    try {
      setProgress(80);

      const resp =
        await searchData({
          user_id: user.id,
          search: searchValue,
          pagination: numRowsPerPage,
          start_date: selectedStartDate
            ? selectedStartDate
            : initialStartDate,
          end_date: selectedEndDate
            ? selectedEndDate
            : initialEndDate,
          status: 'all',
          employee_id: employee_id,
        }).unwrap();

      if (resp.success) {
        setCurrentItems(
          resp.data.tickets.data
        );

        setPageCount(
          Math.ceil(
            resp.data.tickets.total /
              resp.data.tickets.per_page
          )
        );

        setStats(resp.data.stats);
      }

      setProgress(100);
    } catch (err) {
      setInput({
        ...input,
        search: '',
      });

      setProgress(100);

      swal(
        'No results found...'
      );
    }
  };

  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  const formatMoney = (value) => {
    return Number(value ?? 0).toLocaleString(
      'en-ZM',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );
  };

  const getStatusLabel = () => {
    if (selectedStatus === '0') {
      return 'Successful receipts';
    }

    if (selectedStatus === '1') {
      return 'Returned receipts';
    }

    return 'All receipts';
  };

  /*
   * ------------------------------------------------------------
   * UI
   * ------------------------------------------------------------
   */

  return (
    <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() =>
          setProgress(0)
        }
      />

      <div className="w-full px-3 sm:px-4 lg:px-6 pt-4">

        {/* HEADER */}
        <div className="flex items-center gap-3 mb-5">

          <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center shrink-0">
            <ReceiptTaxIcon className="w-6 h-6 text-[#80b643]" />
          </div>

          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              Manage Receipts
            </h1>

            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              View, search and manage ticket receipts
            </p>
          </div>

        </div>

        {/* FILTER CARD */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4 mb-5">

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">

            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Receipt Period
              </p>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Select a date range and employee
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">

              {/* DATE */}
              <div
                className="relative w-full sm:w-auto"
                ref={pickerRef}
              >

                <button
                  type="button"
                  onClick={() =>
                    setOpen(!open)
                  }
                  className="w-full sm:w-auto flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-[#80b643] dark:hover:border-[#80b643] px-3 py-2.5 rounded-lg text-sm text-gray-700 dark:text-gray-200 transition"
                >

                  <div className="flex items-center gap-2">

                    <CalendarIcon className="h-4 w-4 text-[#80b643]" />

                    <span>
                      {format(
                        date_range
                          ? date_range.startDate
                          : state[0].startDate,
                        'MMM dd, yyyy'
                      )}

                      {' → '}

                      {format(
                        date_range
                          ? date_range.endDate
                          : state[0].endDate,
                        'MMM dd, yyyy'
                      )}
                    </span>

                  </div>

                  <ChevronDownIcon
                    className={`h-4 w-4 text-gray-400 transition ${
                      open
                        ? 'rotate-180'
                        : ''
                    }`}
                  />

                </button>

                <AnimatePresence>
                  {open && (
                    <motion.div
                      initial={{
                        opacity: 0,
                        y: -10,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        y: -10,
                      }}
                      transition={{
                        duration: 0.2,
                      }}
                      className="absolute z-50 mt-2 left-0 bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700 rounded-xl p-3 origin-top-left overflow-hidden"
                    >

                      <DateRangePicker
                        onChange={
                          handleSelect
                        }
                        showSelectionPreview={
                          true
                        }
                        moveRangeOnFirstSelection={
                          false
                        }
                        months={1}
                        ranges={state}
                        direction="horizontal"
                      />

                    </motion.div>
                  )}
                </AnimatePresence>

              </div>

              {/* EMPLOYEE */}
              <div className="relative w-full sm:w-auto">

                <UserIcon className="absolute left-3 top-3 h-4 w-4 text-[#80b643] pointer-events-none z-10" />

                <select
                  value={selectedEmployee}
                  onChange={(e) => {
                    setSelectedEmployee(
                      e.target.value
                    );

                    handleEmployeeChange(
                      e,
                      e.target.value
                    );
                  }}
                  className="appearance-none w-full sm:min-w-[190px] bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-[#80b643] dark:hover:border-[#80b643] pl-9 pr-9 py-2.5 rounded-lg text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:border-[#80b643] cursor-pointer transition"
                >

                  <option value="0">
                    All Employees
                  </option>

                  {employees?.data?.data?.map(
                    (item) => (
                      <option
                        key={item.id}
                        value={item.id}
                      >
                        {item.fname}{' '}
                        {item.lname}
                      </option>
                    )
                  )}

                </select>

                <ChevronDownIcon className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />

              </div>

            </div>

          </div>

        </div>

        {/* STATISTICS */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">

          {/* ALL */}
          <button
            type="button"
            onClick={() =>
              handleGetAllReceipts(
                'all'
              )
            }
            className={`text-left bg-white dark:bg-gray-900 border rounded-xl shadow-sm p-4 transition hover:shadow-md ${
              selectedStatus === 'all'
                ? 'border-[#80b643] ring-1 ring-[#80b643]/20'
                : 'border-gray-200 dark:border-gray-800'
            }`}
          >

            <div className="flex items-center justify-between">

              <div>

                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  All Receipts
                </p>

                <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
                  {stats?.total_tickets ??
                    0}
                </p>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Total transactions
                </p>

              </div>

              <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                <BanknoteIcon className="w-6 h-6 text-[#80b643]" />
              </div>

            </div>

          </button>

          {/* SUCCESSFUL */}
          <button
            type="button"
            onClick={() =>
              handGetReceiptsByStatus(
                '0'
              )
            }
            className={`text-left bg-white dark:bg-gray-900 border rounded-xl shadow-sm p-4 transition hover:shadow-md ${
              selectedStatus === '0'
                ? 'border-[#80b643] ring-1 ring-[#80b643]/20'
                : 'border-gray-200 dark:border-gray-800'
            }`}
          >

            <div className="flex items-center justify-between">

              <div>

                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Successful
                </p>

                <p className="text-2xl font-semibold text-gray-700 dark:text-gray-400 mt-1">
                  {stats?.active_tickets ??
                    0}
                </p>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Active ticket sales
                </p>

              </div>

              <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                <BadgeCheckIcon className="w-6 h-6 text-[#80b643]" />
              </div>

            </div>

          </button>

          {/* RETURNS */}
          <button
            type="button"
            onClick={() =>
              handGetReceiptsByStatus(
                '1'
              )
            }
            className={`text-left bg-white dark:bg-gray-900 border rounded-xl shadow-sm p-4 transition hover:shadow-md ${
              selectedStatus === '1'
                ? 'border-[#80b643] ring-1 ring-[#80b643]/20'
                : 'border-gray-200 dark:border-gray-800'
            }`}
          >

            <div className="flex items-center justify-between">

              <div>

                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Returns
                </p>

                <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
                  {stats?.cancelled_tickets ??
                    0}
                </p>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Returned tickets
                </p>

              </div>

              <div className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <XCircleIcon className="w-6 h-6 text-gray-500 dark:text-gray-300" />
              </div>

            </div>

          </button>

        </div>

        {/* TABLE CARD */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

          {/* TABLE HEADER */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-800">

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">

              <div>

                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Receipt Transactions
                </h2>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {getStatusLabel()} for the selected period
                </p>

              </div>

              <div className="flex flex-col sm:flex-row gap-2">

                {/* SEARCH */}
                <form
                  onSubmit={
                    handleSearchInput
                  }
                  className="relative w-full sm:w-64"
                >

                  <SearchIcon className="w-4 h-4 absolute left-3 top-2.5 text-gray-400 pointer-events-none" />

                  <input
                    type="text"
                    name="search"
                    value={input.search}
                    onChange={
                      handleChange
                    }
                    placeholder="Search receipts..."
                    autoComplete="off"
                    className="w-full pl-9 pr-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:border-[#80b643] transition"
                  />

                </form>

                {/* EXPORT */}
                <button
                  type="button"
                  onClick={
                    handleExportCSV
                  }
                  disabled={
                    isExporting ||
                    currentItems.length ===
                      0
                  }
                  className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                    isExporting ||
                    currentItems.length ===
                      0
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                      : 'bg-[#80b643] hover:bg-[#719f3a] text-white shadow-sm'
                  }`}
                >

                  <PrinterIcon className="w-4 h-4" />

                  {isExporting
                    ? 'Exporting...'
                    : 'Export CSV'}

                </button>

              </div>

            </div>

          </div>

          {currentItems.length > 0 ? (

            <>
              {/* TABLE */}
              <div className="overflow-x-auto">

                <table className="w-full min-w-[1000px]">

                  <thead className="bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-700">

                    <tr>

                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Created At
                      </th>

                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Employee
                      </th>

                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Customer
                      </th>

                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Route
                      </th>

                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Departure
                      </th>

                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Type
                      </th>

                      <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Amount
                      </th>

                    </tr>

                  </thead>

                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                    {currentItems.map(
                      (data) => (
                        <tr
                          key={data.id}
                          onClick={() =>
                            openTicketModal(
                              data
                            )
                          }
                          className="group hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer transition"
                        >

                          {/* CREATED */}
                          <td className="px-5 py-4 whitespace-nowrap">

                            <span className="text-xs text-gray-700 dark:text-gray-300">
                              {data.created_at}
                            </span>

                          </td>

                          {/* EMPLOYEE */}
                          <td className="px-5 py-4 whitespace-nowrap">

                            <div className="flex items-center gap-2.5">

                              <div className="w-8 h-8 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center shrink-0">

                                <UserIcon className="w-4 h-4 text-[#80b643]" />

                              </div>

                              <div>

                                <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                                  {data.employee_fname}{' '}
                                  {data.employee_lname}
                                </p>

                              </div>

                            </div>

                          </td>

                          {/* CUSTOMER */}
                          <td className="px-5 py-4">

                            <div className="max-w-[180px]">

                              <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                                {data.full_name}
                              </p>

                              {data.phone && (
                                <p className="text-[11px] text-gray-400 mt-0.5">
                                  {data.phone}
                                </p>
                              )}

                            </div>

                          </td>

                          {/* ROUTE */}
                          <td className="px-5 py-4">

                            <p className="text-xs text-gray-700 dark:text-gray-300 max-w-[180px] truncate">
                              {data.route_name}
                            </p>

                          </td>

                          {/* DEPARTURE */}
                          <td className="px-5 py-4 whitespace-nowrap">

                            <div>

                              <p className="text-xs text-gray-700 dark:text-gray-300">
                                {data.departure_date}
                              </p>

                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {data.departure_time}
                              </p>

                            </div>

                          </td>

                          {/* TYPE */}
                          <td className="px-5 py-4 whitespace-nowrap">

                            {data.cancelled ? (

                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                                Return
                              </span>

                            ) : (

                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#80b643]/10 text-[#80b643]">
                                Sale
                              </span>

                            )}

                          </td>

                          {/* AMOUNT */}
                          <td className="px-5 py-4 whitespace-nowrap text-right">

                            <span
                              className={`text-xs font-semibold ${
                                data.cancelled
                                  ? 'text-gray-600 dark:text-gray-300'
                                  : ''
                              }`}
                            >
                              K{' '}
                              {formatMoney(
                                data.price
                              )}
                            </span>

                          </td>

                        </tr>
                      )
                    )}

                  </tbody>

                </table>

              </div>

              {/* PAGINATION */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 py-4 border-t border-gray-200 dark:border-gray-800">

                <ReactPaginate
                  breakLabel="..."
                  nextLabel={
                    <ChevronRightIcon className="h-5 w-5" />
                  }
                  previousLabel={
                    <ChevronLeftIcon className="h-5 w-5" />
                  }
                  onPageChange={(e) =>
                    handlePageChange(
                      e.selected
                    )
                  }
                  pageRangeDisplayed={3}
                  pageCount={pageCount}
                  forcePage={currentPage}
                  renderOnZeroPageCount={
                    null
                  }
                  containerClassName="pagination"
                  pageLinkClassName="page-num"
                  previousLinkClassName="controlBtn"
                  nextLinkClassName="controlBtn"
                  activeLinkClassName={`${
                    darkMode
                      ? ''
                      : 'active'
                  } dark:text-white bg-white dark:bg-gray-700 hover:dark:text-white hover:dark:bg-gray-600`}
                />

                <div className="flex items-center gap-2">

                  <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    Rows per page
                  </span>

                  <select
                    value={
                      numRowsPerPage
                    }
                    onChange={(e) =>
                      onChangePageRows(
                        e.target.value
                      )
                    }
                    className="w-20 px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#80b643]"
                  >

                    <option value="10">
                      10
                    </option>

                    <option value="25">
                      25
                    </option>

                    <option value="50">
                      50
                    </option>

                    <option value="100">
                      100
                    </option>

                  </select>

                </div>

              </div>

            </>

          ) : (

            /* EMPTY STATE */
            <div className="py-16 px-4 text-center">

              <div className="w-14 h-14 mx-auto rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">

                <ReceiptTaxIcon className="w-7 h-7 text-gray-400 dark:text-gray-500" />

              </div>

              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                No receipts found
              </h3>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
                There are no receipt transactions matching the selected filters.
              </p>

            </div>

          )}

        </div>

      </div>

      {/* =====================================================
          RECEIPT MODAL
          ===================================================== */}

      <AnimatePresence>

        {isModalOpen &&
          selectedTicket && (
            <>
              {/* OVERLAY */}
              <motion.div
                className="fixed inset-0 bg-black/40 backdrop-blur-[1px] z-40"
                onClick={
                  closeTicketModal
                }
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
              />

              {/* MODAL */}
              <motion.div
                initial={{
                  opacity: 0,
                  y: 30,
                  scale: 0.96,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                }}
                exit={{
                  opacity: 0,
                  y: 30,
                  scale: 0.96,
                }}
                transition={{
                  duration: 0.2,
                }}
                className="fixed z-50 inset-x-3 bottom-3 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[420px] max-h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden"
              >

                {/* MODAL HEADER */}
                <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-gray-200 dark:border-gray-800">

                  <div className="flex items-center gap-3">

                    <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">

                      <ReceiptTaxIcon className="w-5 h-5 text-[#80b643]" />

                    </div>

                    <div>

                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Ticket Receipt
                      </h3>

                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        Receipt #{selectedTicket.id}
                      </p>

                    </div>

                  </div>

                  <button
                    type="button"
                    onClick={
                      closeTicketModal
                    }
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-white transition"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>

                </div>

                {/* RECEIPT CONTENT */}
                <div className="overflow-y-auto max-h-[calc(90vh-130px)] p-4 sm:p-5">

                  {/* RECEIPT PREVIEW */}
                  <div className="bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl p-4">

                    {/* RECEIPT TITLE */}
                    <div className="text-center pb-3 mb-3 border-b border-dashed border-gray-300 dark:border-gray-600">

                      <div className="w-10 h-10 mx-auto rounded-full bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center mb-2">

                        <ReceiptTaxIcon className="w-5 h-5 text-[#80b643]" />

                      </div>

                      <h4 className="text-xs font-bold uppercase tracking-wide text-gray-800 dark:text-white">
                        Bus Ticket Receipt
                      </h4>

                      <p className="text-[10px] text-gray-400 mt-1">
                        {selectedTicket.ticket_token}
                      </p>

                    </div>

                    {/* GENERAL DETAILS */}
                    <div className="space-y-2 text-[11px]">

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Created at
                        </span>

                        <span className="text-right font-medium text-gray-800 dark:text-gray-200">
                          {selectedTicket.created_at}
                        </span>

                      </div>

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Employee
                        </span>

                        <span className="text-right font-medium text-gray-800 dark:text-gray-200">
                          {selectedTicket.employee_fname}{' '}
                          {selectedTicket.employee_lname}
                        </span>

                      </div>

                    </div>

                    <div className="border-t border-dashed border-gray-300 dark:border-gray-600 my-4" />

                    {/* PASSENGER */}
                    <div className="space-y-2 text-[11px]">

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Passenger
                        </span>

                        <span className="text-right font-medium text-gray-800 dark:text-gray-200 max-w-[60%] break-words">
                          {selectedTicket.full_name}
                        </span>

                      </div>

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Phone
                        </span>

                        <span className="text-right text-gray-800 dark:text-gray-200">
                          {selectedTicket.phone}
                        </span>

                      </div>

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Route
                        </span>

                        <span className="text-right font-medium text-gray-800 dark:text-gray-200 max-w-[60%] break-words">
                          {selectedTicket.route_name}
                        </span>

                      </div>

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Departure
                        </span>

                        <span className="text-right text-gray-800 dark:text-gray-200">
                          {selectedTicket.departure_date}{' '}
                          @{selectedTicket.departure_time}
                        </span>

                      </div>

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Seat No
                        </span>

                        <span className="text-right font-semibold text-gray-800 dark:text-gray-200">
                          {selectedTicket.seat_number ||
                            '—'}
                        </span>

                      </div>

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Ticket No
                        </span>

                        <span className="text-right font-semibold text-gray-800 dark:text-gray-200 max-w-[60%] break-all">
                          {selectedTicket.ticket_token}
                        </span>

                      </div>

                    </div>

                    <div className="border-t border-dashed border-gray-300 dark:border-gray-600 my-4" />

                    {/* PAYMENT */}
                    <div className="space-y-2 text-[11px]">

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Payment Type
                        </span>

                        <span className="text-right font-medium text-gray-800 dark:text-gray-200">
                          {selectedTicket.payment_type_name}
                        </span>

                      </div>

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Gross Total
                        </span>

                        <span className="text-right text-gray-800 dark:text-gray-200">
                          K{' '}
                          {formatMoney(
                            Number(
                              selectedTicket.price
                            ) +
                              Number(
                                selectedTicket.discount
                              )
                          )}
                        </span>

                      </div>

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Discount
                        </span>

                        <span className="text-right text-gray-800 dark:text-gray-200">
                          K{' '}
                          {formatMoney(
                            selectedTicket.discount
                          )}
                        </span>

                      </div>

                      {selectedTicket.tax_id ? (
                        <div className="flex justify-between gap-4">

                          <span className="text-gray-500 dark:text-gray-400">
                            {selectedTicket.tax_name}{' '}
                            ({selectedTicket.tax_rate}%)
                          </span>

                          <span className="text-right text-gray-800 dark:text-gray-200">
                            K{' '}
                            {formatMoney(
                              selectedTicket.tax
                            )}
                          </span>

                        </div>
                      ) : null}

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Tendered
                        </span>

                        <span className="text-right text-gray-800 dark:text-gray-200">
                          K{' '}
                          {formatMoney(
                            Number(
                              selectedTicket.change
                            ) +
                              Number(
                                selectedTicket.price
                              )
                          )}
                        </span>

                      </div>

                      <div className="flex justify-between gap-4">

                        <span className="text-gray-500 dark:text-gray-400">
                          Change
                        </span>

                        <span className="text-right text-gray-800 dark:text-gray-200">
                          K{' '}
                          {formatMoney(
                            selectedTicket.change
                          )}
                        </span>

                      </div>

                      <div className="flex justify-between gap-4 pt-2 mt-2 border-t border-gray-200 dark:border-gray-700">

                        <span className="font-semibold text-gray-700 dark:text-gray-200">
                          Net Total
                        </span>

                        <span className="font-bold">
                          K{' '}
                          {formatMoney(
                            selectedTicket.price
                          )}
                        </span>

                      </div>

                    </div>

                    {/* STATUS */}
                    <div className="mt-4 flex items-center justify-between">

                      <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        Status
                      </span>

                      {selectedTicket.cancelled ? (

                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                          Returned
                        </span>

                      ) : (

                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold bg-[#80b643]/10 text-[#80b643]">
                          Valid
                        </span>

                      )}

                    </div>

                    {selectedTicket.cancelled ? (
                      <div className="mt-2 flex justify-between gap-4 text-[11px]">

                        <span className="text-gray-500 dark:text-gray-400">
                          Cancelled By
                        </span>

                        <span className="text-right text-gray-700 dark:text-gray-300 break-all">
                          {selectedTicket.cancelled_by}
                          {' @'}
                          {selectedTicket.updated_at}
                        </span>

                      </div>
                    ) : null}

                  </div>

                  {/* HIDDEN PRINT REF */}
                  <div
                    ref={printRef}
                    className="hidden"
                  >
                    <div className="ticket">
                      <div className="title">
                        BUS TICKET RECEIPT
                      </div>

                      <div className="line"></div>

                      <div className="info">
                        <strong>
                          Ticket No:
                        </strong>{' '}
                        {
                          selectedTicket.ticket_token
                        }
                      </div>

                      <div className="info">
                        <strong>
                          Passenger:
                        </strong>{' '}
                        {
                          selectedTicket.full_name
                        }
                      </div>

                      <div className="info">
                        <strong>
                          Employee:
                        </strong>{' '}
                        {
                          selectedTicket.employee_fname
                        }{' '}
                        {
                          selectedTicket.employee_lname
                        }
                      </div>

                      <div className="info">
                        <strong>
                          Route:
                        </strong>{' '}
                        {
                          selectedTicket.route_name
                        }
                      </div>

                      <div className="info">
                        <strong>
                          Departure:
                        </strong>{' '}
                        {
                          selectedTicket.departure_date
                        }{' '}
                        {
                          selectedTicket.departure_time
                        }
                      </div>

                      <div className="info">
                        <strong>
                          Seat No:
                        </strong>{' '}
                        {
                          selectedTicket.seat_number
                        }
                      </div>

                      <div className="line"></div>

                      <div className="info">
                        <strong>
                          Fare:
                        </strong>{' '}
                        K
                        {
                          selectedTicket.price
                        }
                      </div>

                      <div className="info">
                        <strong>
                          Status:
                        </strong>{' '}
                        {
                          selectedTicket.cancelled
                            ? 'Cancelled'
                            : 'Valid'
                        }
                      </div>

                    </div>
                  </div>

                </div>

                {/* MODAL FOOTER */}
                <div className="flex gap-2 px-4 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">

                  <button
                    type="button"
                    onClick={
                      handlePrint
                    }
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition"
                  >

                    <PrinterIcon className="w-4 h-4" />

                    Print Receipt

                  </button>

                  <button
                    type="button"
                    onClick={
                      closeTicketModal
                    }
                    className="px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition"
                  >
                    Close
                  </button>

                </div>

              </motion.div>
            </>
          )}

      </AnimatePresence>

    </div>
  );
}

export default ManageReceiptsView;